/**
 * Robotics Proposal Requests API.
 *
 * Submissions from the "Request a Robotics Proposal" form on the public website,
 * pushed in via POST /api/public/robotics-proposals (same API key as the website
 * form leads). Values are sanitized server-side before storage; everything here is
 * the read/triage side used by the Marketing page.
 *
 * The submission itself is never editable — it is the record of what the visitor
 * sent. Staff add notes, void it with a reason, or accept it into the client
 * database as a lead.
 */

import { API_BASE } from '../config/apiBase';
import { duplicateClientError } from './clients';
import { CAMPAIGN_ATTRIBUTION_LABELS, type CampaignAttribution } from './campaignAttribution';

export type RoboticsProposalStatus = 'Pending' | 'Approved' | 'Voided';

export const ROBOTICS_PROPOSAL_STATUSES: RoboticsProposalStatus[] = [
  'Pending',
  'Approved',
  'Voided',
];

export interface RoboticsProposalRow extends CampaignAttribution {
  id: number;

  // Contact Information
  contact_name: string;
  job_title: string;
  company_name: string;
  email: string;
  phone: string;
  company_address: string;
  deployment_location: string;

  // Organization Information. The `_other` fields hold the free-text follow-up the
  // form reveals when the dropdown above is answered "Other".
  industry: string;
  industry_other: string;
  primary_business_activity: string;
  primary_business_activity_other: string;
  currently_using_robotics: string;
  existing_robotics_details: string;

  // Robotics & Project Requirements
  solutions_of_interest: string[];
  solution_of_interest_other: string;
  operational_tasks: string;
  expected_timeline: string;

  // Facility Infrastructure
  facility_has_elevators: string;
  elevator_access_needed: string;
  number_of_elevators: string;
  elevator_manufacturer: string;
  elevator_manufacturer_other: string;
  /** Who services the elevators. Asked only when the facility has them. */
  elevator_company_name: string;
  elevator_company_phone: string;
  elevator_technician_name: string;
  elevator_technician_phone: string;
  has_service_elevator: string;
  has_trash_chute: string;
  has_laundry_chute: string;
  unique_facility_considerations: string;

  // Additional Information & Follow-Up
  preferred_followup_method: string;
  preferred_followup_time: string;
  additional_comments: string;
  consent_acknowledged: boolean;
  sms_marketing_consent: boolean;

  // Triage
  status: RoboticsProposalStatus;
  /** Set once the submission has been added to the client database. */
  converted_client_id: number | null;
  converted_client_company: string | null;
  status_changed_by_user_id: number | null;
  status_changed_by: string | null;
  status_changed_at: string | null;
  void_reason: string;

  submitted_ip: string;
  note_count: number;
  created_at: string | null;
  updated_at: string | null;
  /**
   * Original request body, only fetched with the detail view. Useful when the
   * website adds a question the portal does not have a column for yet.
   */
  raw_payload?: Record<string, string>;
}

export interface RoboticsProposalNote {
  id: number;
  proposal_id: number;
  user_id: number | null;
  submitted_by: string | null;
  note: string;
  created_at: string | null;
  updated_at: string | null;
}

/** How a field's value should be rendered on the detail page. */
type ProposalFieldKind = 'text' | 'long' | 'list' | 'bool';

export interface ProposalField {
  key: keyof RoboticsProposalRow;
  label: string;
  kind?: ProposalFieldKind;
  /**
   * A conditional "Please Specify" that the form only shows for one answer. Hidden
   * when empty, so an unanswered follow-up doesn't add a dash to every submission.
   */
  followUp?: boolean;
}

export interface ProposalSection {
  title: string;
  fields: ProposalField[];
}

/**
 * The form's own five sections, in page order, with the labels the visitor saw.
 *
 * The detail page renders straight from this list, so adding a question to the form
 * is a column, an intake alias and one entry here — not a new block of JSX.
 */
export const PROPOSAL_SECTIONS: ProposalSection[] = [
  {
    title: 'Contact Information',
    fields: [
      { key: 'contact_name', label: 'Contact Name' },
      { key: 'job_title', label: 'Job Title' },
      { key: 'company_name', label: 'Company Name' },
      { key: 'email', label: 'Email Address' },
      { key: 'phone', label: 'Phone Number' },
      { key: 'company_address', label: 'Company Address' },
      { key: 'deployment_location', label: 'Where will the robotics solution be deployed?' },
    ],
  },
  {
    title: 'Organization Information',
    fields: [
      { key: 'industry', label: 'Industry' },
      { key: 'industry_other', label: 'Industry — please specify', followUp: true },
      { key: 'primary_business_activity', label: 'Primary Business Activity' },
      {
        key: 'primary_business_activity_other',
        label: 'Primary Business Activity — please specify',
        followUp: true,
      },
      {
        key: 'currently_using_robotics',
        label: 'Currently using robotics or automation at this facility?',
      },
      {
        key: 'existing_robotics_details',
        label: 'Systems currently in use',
        kind: 'long',
        followUp: true,
      },
    ],
  },
  {
    title: 'Robotics & Project Requirements',
    fields: [
      {
        key: 'solutions_of_interest',
        label: 'Type of robotic solution or service of interest',
        kind: 'list',
      },
      {
        key: 'solution_of_interest_other',
        label: 'Solution or service — please specify',
        followUp: true,
      },
      {
        key: 'operational_tasks',
        label: 'What operational tasks are you looking to automate?',
        kind: 'long',
      },
      { key: 'expected_timeline', label: 'Expected purchase or implementation timeline' },
    ],
  },
  {
    title: 'Facility Infrastructure',
    fields: [
      { key: 'facility_has_elevators', label: 'Does your facility have elevators?' },
      {
        key: 'elevator_access_needed',
        label: 'Which elevators would the robots need to access?',
        followUp: true,
      },
      {
        key: 'number_of_elevators',
        label: 'Approximately how many elevators are in the facility?',
        followUp: true,
      },
      { key: 'elevator_manufacturer', label: 'Elevator Manufacturer', followUp: true },
      {
        key: 'elevator_manufacturer_other',
        label: 'Elevator Manufacturer — please specify',
        followUp: true,
      },
      { key: 'elevator_company_name', label: 'Elevator Company', followUp: true },
      { key: 'elevator_company_phone', label: 'Elevator Company Phone', followUp: true },
      { key: 'elevator_technician_name', label: 'Elevator Technician', followUp: true },
      { key: 'elevator_technician_phone', label: 'Elevator Technician Phone', followUp: true },
      { key: 'has_service_elevator', label: 'Does the property have a dedicated service elevator?' },
      { key: 'has_trash_chute', label: 'Does the property have a trash chute?' },
      { key: 'has_laundry_chute', label: 'Does the property have a laundry chute?' },
      {
        key: 'unique_facility_considerations',
        label: 'Any unique facility considerations we should know about?',
        kind: 'long',
      },
    ],
  },
  {
    title: 'Additional Information & Follow-Up',
    fields: [
      { key: 'preferred_followup_method', label: 'Preferred Method of Follow-Up' },
      { key: 'preferred_followup_time', label: 'Preferred Time for Follow-Up' },
      {
        key: 'additional_comments',
        label: 'Additional comments, questions, or special requirements',
        kind: 'long',
      },
      { key: 'consent_acknowledged', label: 'Consent acknowledged', kind: 'bool' },
      { key: 'sms_marketing_consent', label: 'SMS marketing consent', kind: 'bool' },
    ],
  },
  {
    title: 'Campaign Attribution',
    fields: CAMPAIGN_ATTRIBUTION_LABELS.map(({ key, label }) => ({ key, label, followUp: true })),
  },
];

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw (
      duplicateClientError(res.status, data) ??
      new Error((data as { error?: string }).error || fallback)
    );
  }
  return data as T;
}

export async function fetchRoboticsProposals(userId: number): Promise<RoboticsProposalRow[]> {
  const res = await fetch(
    `${API_BASE}/api/marketing/robotics-proposals?userId=${encodeURIComponent(userId)}`
  );
  const data = await jsonOrThrow<{ proposals: RoboticsProposalRow[] }>(
    res,
    'Failed to fetch robotics proposal requests'
  );
  return data.proposals ?? [];
}

/** The full submission plus its notes, for the detail page. */
export async function fetchRoboticsProposal(
  id: number,
  userId: number
): Promise<{ proposal: RoboticsProposalRow; notes: RoboticsProposalNote[] }> {
  const res = await fetch(
    `${API_BASE}/api/marketing/robotics-proposals/${id}?userId=${encodeURIComponent(userId)}`
  );
  const data = await jsonOrThrow<{
    proposal: RoboticsProposalRow;
    notes: RoboticsProposalNote[];
  }>(res, 'Failed to fetch the proposal request');
  return { proposal: data.proposal, notes: data.notes ?? [] };
}

/**
 * Triage only. Approved is set by the convert call, so the statuses reachable here
 * are Pending and Voided; voiding needs a reason.
 */
export async function updateRoboticsProposalStatus(
  id: number,
  payload: { user_id: number; status: RoboticsProposalStatus; void_reason?: string }
): Promise<RoboticsProposalRow> {
  const res = await fetch(`${API_BASE}/api/marketing/robotics-proposals/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await jsonOrThrow<{ proposal: RoboticsProposalRow }>(
    res,
    'Failed to update the proposal request'
  );
  return data.proposal;
}

export async function createRoboticsProposalNote(
  id: number,
  note: string,
  userId: number
): Promise<RoboticsProposalNote> {
  const res = await fetch(`${API_BASE}/api/marketing/robotics-proposals/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId }),
  });
  return jsonOrThrow(res, 'Failed to add the note');
}

export async function updateRoboticsProposalNote(
  id: number,
  noteId: number,
  note: string,
  userId: number
): Promise<RoboticsProposalNote> {
  const res = await fetch(`${API_BASE}/api/marketing/robotics-proposals/${id}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId }),
  });
  return jsonOrThrow(res, 'Failed to update the note');
}

export async function deleteRoboticsProposalNote(
  id: number,
  noteId: number,
  userId: number
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/marketing/robotics-proposals/${id}/notes/${noteId}?userId=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  await jsonOrThrow(res, 'Failed to delete the note');
}

/** Values the verification step shows and the user may correct before saving. */
export interface ConvertRoboticsProposalPayload {
  user_id: number;
  company: string;
  point_of_contact: string;
  contact_email?: string;
  contact_phone?: string;
  source?: string;
  industry?: string;
  notes?: string;
}

/**
 * Adds the submission to the client database as a lead. The submission flips to
 * Approved and the sales team is emailed.
 */
export async function convertRoboticsProposal(
  id: number,
  payload: ConvertRoboticsProposalPayload
): Promise<{ proposal: RoboticsProposalRow; client_id: number }> {
  const res = await fetch(`${API_BASE}/api/marketing/robotics-proposals/${id}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to add lead to the client database');
}

/** Path to a submission's detail page. */
export function roboticsProposalPath(id: number): string {
  return `/marketing/robotics-proposal/${id}`;
}

/** Best available name for the person who submitted, for headings and tables. */
export function proposalContactName(p: RoboticsProposalRow): string {
  if (p.contact_name.trim()) return p.contact_name.trim();
  if (p.email) return p.email.split('@')[0];
  return 'Unknown';
}

/** Company, falling back to the contact so a row is never blank. */
export function proposalCompanyLabel(p: RoboticsProposalRow): string {
  return p.company_name.trim() || proposalContactName(p);
}

/** Industry with the "Other" follow-up folded in, since "Other" alone says nothing. */
export function proposalIndustryLabel(p: RoboticsProposalRow): string {
  const industry = p.industry.trim();
  const other = p.industry_other.trim();
  if (industry && other) return /^other/i.test(industry) ? other : `${industry} — ${other}`;
  return industry || other;
}

/** One lowercased haystack per submission, so the search box covers every answer. */
export function proposalSearchText(p: RoboticsProposalRow): string {
  const values: string[] = [];
  for (const section of PROPOSAL_SECTIONS) {
    for (const field of section.fields) {
      const value = p[field.key];
      if (Array.isArray(value)) values.push(value.join(' '));
      else if (typeof value === 'string') values.push(value);
    }
  }
  values.push(p.status, p.void_reason, String(p.id));
  return values.join(' ').toLowerCase();
}

/**
 * Seeds the verification step from the submission.
 *
 * The lead only has one notes field, so the answers that don't map to a client
 * column are summarized into it — a salesperson opening the lead should see what
 * the prospect asked for without coming back here.
 */
export function draftFromProposal(p: RoboticsProposalRow): {
  company: string;
  point_of_contact: string;
  contact_email: string;
  contact_phone: string;
  source: string;
  industry: string;
  notes: string;
} {
  const activity = /^other/i.test(p.primary_business_activity)
    ? p.primary_business_activity_other
    : p.primary_business_activity;

  const solutions = [...p.solutions_of_interest];
  const otherIndex = solutions.findIndex((s) => /^other/i.test(s));
  if (otherIndex >= 0 && p.solution_of_interest_other.trim()) {
    solutions[otherIndex] = p.solution_of_interest_other.trim();
  }

  const lines: string[] = [];
  const add = (label: string, value: string) => {
    if (value && value.trim()) lines.push(`${label}: ${value.trim()}`);
  };

  if (p.operational_tasks.trim()) lines.push(`Tasks to automate: ${p.operational_tasks.trim()}`);
  add('Solutions of interest', solutions.join(', '));
  add('Timeline', p.expected_timeline);
  add('Job title', p.job_title);
  add('Deployment location', p.deployment_location);
  add('Company address', p.company_address);
  add('Primary business activity', activity);
  add('Currently using robotics', p.currently_using_robotics);
  add('Existing systems', p.existing_robotics_details);
  add('Elevators', p.facility_has_elevators);
  add('Elevator access needed', p.elevator_access_needed);
  add('Number of elevators', p.number_of_elevators);
  add(
    'Elevator manufacturer',
    /^other/i.test(p.elevator_manufacturer)
      ? p.elevator_manufacturer_other
      : p.elevator_manufacturer
  );
  // Carried over because access for a survey or an install is arranged through
  // these two, so whoever picks up the lead should not have to come back here.
  add(
    'Elevator company',
    [p.elevator_company_name, p.elevator_company_phone].filter(Boolean).join(' — ')
  );
  add(
    'Elevator technician',
    [p.elevator_technician_name, p.elevator_technician_phone].filter(Boolean).join(' — ')
  );
  add('Dedicated service elevator', p.has_service_elevator);
  add('Trash chute', p.has_trash_chute);
  add('Laundry chute', p.has_laundry_chute);
  add('Unique facility considerations', p.unique_facility_considerations);
  add('Preferred follow-up', [p.preferred_followup_method, p.preferred_followup_time].filter(Boolean).join(', '));
  if (p.additional_comments.trim()) {
    lines.push(`Additional comments: ${p.additional_comments.trim()}`);
  }

  return {
    company: proposalCompanyLabel(p),
    point_of_contact: proposalContactName(p),
    contact_email: p.email ?? '',
    contact_phone: p.phone ?? '',
    source: 'Website — Robotics Proposal Request',
    industry: proposalIndustryLabel(p),
    notes: lines.join('\n'),
  };
}
